/**
 * Error presentation policy: raw error messages from the API, Cognito, or the
 * OIDC library are log lines, not UI copy. Every user-facing error surface
 * gets its copy from this module and logs the original via console.error.
 *
 * Classification trusts the most reliable signal first:
 *   1. the API's machine-readable `code` (createErrorResponse's third field);
 *   2. the HTTP status an ApiError carries;
 *   3. message wording, ONLY to refine within a status (which field a 409
 *      names, which licence refusal a 400 is) or for errors with no status at
 *      all (Cognito, network, OIDC, plain strings).
 * Pages that need their own copy or behaviour branch on
 * `classifyError(err).kind` / `.field` / `.code` — never on `err.status` or the
 * message text.
 */

import { formatNumber } from './formatters';

export type ErrorKind =
  | 'network'
  | 'unauthenticated'
  | 'forbidden'
  | 'not_found'
  | 'conflict'
  | 'validation'
  | 'rate_limited'
  | 'server'
  | 'unknown';

/** The unique key a conflict names, when the API (or its wording) says which. */
export type ErrorField = 'slug' | 'name' | 'alias';

export interface ErrorClass {
  kind: ErrorKind;
  /** Set only on a conflict that names one field (a taken slug, name or alias). */
  field?: ErrorField;
  /** The API's machine-readable code, when the response carried one. */
  code?: string;
}

const MESSAGES = {
  network: 'Could not reach the server. Check your connection and try again.',
  unauthenticated: 'Your session has expired. Sign in again to continue.',
  forbidden: 'You do not have access to this in the current organization.',
  not_found: 'That item no longer exists. It may have been deleted.',
  conflict: 'This conflicts with the current state of this item. Refresh and try again.',
  validation: 'Some of the details are not valid. Check them and try again.',
  rate_limited: 'Too many requests. Wait a moment and try again.',
  server: 'The server hit an unexpected problem. Try again in a moment.',
  unknown: 'Something went wrong. Please try again.',
} satisfies Record<ErrorKind, string>;

const fieldConflictMessage = (field: ErrorField) => `That ${field} is already in use. Choose a different one.`;

type Row = { pattern: RegExp; message: string | ((match: RegExpExecArray) => string) };

/**
 * The API's known refusals (409s). They name what is in the way, and some
 * embed names a user chose ("sole member of organization "Not Found Ltd"") that
 * would otherwise be caught by wording rows below. Each maps to copy that names
 * the blocker and the next step, never "try again".
 */
const REFUSAL_ROWS: Row[] = [
  {
    // Names how many and where they are (DESIGN_SYSTEM §5 dependents refusals):
    // the only surface that deletes an app client is its record dialog.
    pattern: /still has (\d+) active subscription/i,
    message: (match) => {
      const count = Number(match[1]);
      return `This app client still has ${formatNumber(count)} subscription${count === 1 ? '' : 's'}. Delete ${count === 1 ? 'it' : 'them'} from the Subscriptions section first.`;
    },
  },
  { pattern: /publisher credentials cannot be deleted/i, message: "A template's publisher credential is deleted with its template." },
  { pattern: /sole member of/i, message: 'This user is the only member of an organization. Delete that organization first.' },
  { pattern: /only owner of this organization/i, message: "This member is the organization's only owner, so they can't be removed." },
  { pattern: /last one who can manage members and roles/i, message: 'This member is the last one who can manage members and roles here. Give that permission to another member first.' },
  { pattern: /last one granting permission to manage members/i, message: 'This role is the last one that lets anyone manage members and roles here. Give that permission to another member first.' },
  { pattern: /organization still has .*delete them before deleting/i, message: 'This organization still owns records. Delete them first, then delete the organization.' },
  { pattern: /already a member of this organization/i, message: 'That person is already a member of this organization.' },
];

/**
 * The three licence-key refusals. They must stay distinct, because "wrong key",
 * "expired key" and "used up key" send the person to different places.
 */
const LICENSE_ROWS: Row[] = [
  { pattern: /license key has expired/i, message: 'That license key has expired. Ask your administrator for a new one.' },
  { pattern: /license key has already been used/i, message: 'That license key has already been used the maximum number of times. Ask your administrator for a new one.' },
  { pattern: /invalid.*license/i, message: 'That license key is not valid. Check it and try again.' },
];

/** Cognito account flows (AccountSettings talks to Cognito directly; no status). */
const COGNITO_ROWS: Row[] = [
  { pattern: /NotAuthorizedException|incorrect.*password/i, message: 'The current password you entered is incorrect.' },
  { pattern: /InvalidPasswordException/i, message: 'The new password does not meet the password requirements.' },
  { pattern: /LimitExceededException/i, message: 'Too many attempts. Wait a few minutes and try again.' },
  { pattern: /CodeMismatchException/i, message: 'That verification code is incorrect. Check the code and try again.' },
  { pattern: /ExpiredCodeException/i, message: 'That verification code has expired. Request a new one.' },
  { pattern: /AliasExistsException/i, message: 'An account with that email address already exists.' },
];

const NETWORK_PATTERN = /networkerror|failed to fetch|load failed/i;
const UNIQUE_WORDING = /already exists|already in use|not available|duplicate/i;

/** Codes the API sends today (api/src), mapped to a kind. Unknown codes fall through to the status. */
const CODE_KINDS: Record<string, ErrorClass> = {
  SLUG_TAKEN: { kind: 'conflict', field: 'slug' },
  NAME_TAKEN: { kind: 'conflict', field: 'name' },
  ALIAS_TAKEN: { kind: 'conflict', field: 'alias' },
  INVALID_REQUEST: { kind: 'validation' },
  INVALID_REGION: { kind: 'validation' },
  REGION_TOO_FINE: { kind: 'validation' },
  TOO_MANY_SENSORS: { kind: 'validation' },
  BROKER_UNAVAILABLE: { kind: 'server' },
  // Sensor writes (POST /sensors, PUT /sensors/{id}). The three refusals belong
  // under a field — `sensorFieldRefusals` below reads them for it; the copy here
  // is only for a surface that does not.
  INVALID_CREDENTIALS: { kind: 'validation' },
  CREDENTIALS_IN_CONNECTION_CONFIG: { kind: 'validation' },
  INVALID_STREAM_URL: { kind: 'validation' },
  // The secret store refused the device login; the API changed nothing.
  CREDENTIAL_STORE_UNAVAILABLE: { kind: 'server' },
};

/**
 * Copy for a code whose kind's generic sentence would hide the next step. A
 * code without a line here reads its kind's message.
 */
const CODE_MESSAGES: Partial<Record<string, string>> = {
  INVALID_CREDENTIALS: "The device login isn't valid. Check the username and password.",
  CREDENTIALS_IN_CONNECTION_CONFIG:
    'The connection config holds a login or secret. Remove it, and enter the username and password under Device login.',
  INVALID_STREAM_URL: "The stream URL in the connection config isn't a valid URL.",
  CREDENTIAL_STORE_UNAVAILABLE: "The device login couldn't be saved, so nothing was changed. Try again in a minute.",
};

function rawMessage(error: unknown): string {
  return error instanceof Error ? error.message : typeof error === 'string' ? error : '';
}

/**
 * The status and code an ApiError carries, read structurally rather than with
 * `instanceof ApiError`: many test suites mock services/api without exporting
 * the class, and importing it here as a value would break every one of them.
 */
function transportFields(error: unknown): { status?: number; code?: string } {
  if (!(error instanceof Error)) return {};
  const { status, code } = error as { status?: unknown; code?: unknown };
  return {
    status: typeof status === 'number' ? status : undefined,
    code: typeof code === 'string' && code !== '' ? code : undefined,
  };
}

function firstMatch(rows: Row[], raw: string): string | null {
  for (const { pattern, message } of rows) {
    const match = pattern.exec(raw);
    if (match) return typeof message === 'function' ? message(match) : message;
  }
  return null;
}

function kindForStatus(status: number): ErrorKind {
  if (status === 400 || status === 422) return 'validation';
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 409) return 'conflict';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'server';
  return 'unknown';
}

function fieldFromWording(raw: string): ErrorField | undefined {
  if (!UNIQUE_WORDING.test(raw)) return undefined;
  if (/\bslug\b/i.test(raw)) return 'slug';
  if (/\balias\b/i.test(raw)) return 'alias';
  if (/\bname\b/i.test(raw)) return 'name';
  return undefined;
}

interface Described extends ErrorClass {
  message: string;
}

function describe(error: unknown): Described {
  const raw = rawMessage(error);
  const { status, code } = transportFields(error);

  // 1. The API's code.
  const byCode = code ? CODE_KINDS[code] : undefined;
  if (code && byCode) {
    const message = byCode.field
      ? fieldConflictMessage(byCode.field)
      : CODE_MESSAGES[code] ?? MESSAGES[byCode.kind];
    return { ...byCode, code, message };
  }

  // 2. The HTTP status, refined only by wording within it.
  if (status !== undefined) {
    const kind = kindForStatus(status);
    const base = { kind, ...(code ? { code } : {}) };
    if (kind === 'conflict') {
      const refusal = firstMatch(REFUSAL_ROWS, raw);
      if (refusal) return { ...base, message: refusal };
      const field = fieldFromWording(raw);
      if (field) return { ...base, field, message: fieldConflictMessage(field) };
    }
    if (status === 400 || status === 403) {
      const license = firstMatch(LICENSE_ROWS, raw);
      if (license) return { ...base, message: license };
    }
    return { ...base, message: MESSAGES[kind] };
  }

  // 3. No status: a network failure, Cognito, OIDC, or a plain string. Wording only.
  const refusal = firstMatch(REFUSAL_ROWS, raw);
  if (refusal) return { kind: 'conflict', message: refusal };
  if (NETWORK_PATTERN.test(raw)) return { kind: 'network', message: MESSAGES.network };
  const cognito = firstMatch(COGNITO_ROWS, raw);
  if (cognito) {
    const kind: ErrorKind = /LimitExceeded/i.test(raw) ? 'rate_limited' : 'validation';
    return { kind, message: cognito };
  }
  const license = firstMatch(LICENSE_ROWS, raw);
  if (license) return { kind: 'validation', message: license };
  if (/status:\s*401|unauthorized|token.*expired|invalid.*token/i.test(raw)) {
    return { kind: 'unauthenticated', message: MESSAGES.unauthenticated };
  }
  if (/status:\s*403|forbidden|missing permission|not authorized/i.test(raw)) {
    return { kind: 'forbidden', message: MESSAGES.forbidden };
  }
  if (/status:\s*404|not found/i.test(raw)) return { kind: 'not_found', message: MESSAGES.not_found };
  if (/already exists|duplicate/i.test(raw)) {
    const field = fieldFromWording(raw) ?? 'name';
    return { kind: 'conflict', field, message: fieldConflictMessage(field) };
  }
  if (/status:\s*409/i.test(raw)) return { kind: 'conflict', message: MESSAGES.conflict };
  if (/status:\s*429|too many requests|rate limit/i.test(raw)) {
    return { kind: 'rate_limited', message: MESSAGES.rate_limited };
  }
  if (/status:\s*5\d\d/i.test(raw)) return { kind: 'server', message: MESSAGES.server };
  return { kind: 'unknown', message: MESSAGES.unknown };
}

/**
 * What kind of failure this is: `{ kind, field?, code? }`. Pages branch on this,
 * never on `err.status` or the message.
 */
export function classifyError(error: unknown): ErrorClass {
  const { message: _message, ...rest } = describe(error);
  return rest;
}

/**
 * The kind plus the one sentence a user sees for it, with no context prefix.
 * ErrorState renders this beneath its title.
 */
export function describeError(error: unknown): { kind: ErrorKind; message: string } {
  const { kind, message } = describe(error);
  return { kind, message };
}

/** Kinds a Retry can never fix: the refusal or the absence is permanent. */
export const UNRETRYABLE_KINDS: readonly ErrorKind[] = ['forbidden', 'not_found', 'unauthenticated'];

/**
 * Convert an unknown thrown value into copy that is safe to show a user, for
 * untitled surfaces (toasts, inline Alerts, FormError, Field errors).
 * @param error   the caught value (unknown — narrow inside)
 * @param context optional lead-in, e.g. "Couldn't create the sensor"
 */
export function humanizeError(error: unknown, context?: string): string {
  const human = describe(error).message;
  return context ? `${context}. ${human}` : human;
}

/**
 * The lens aliases blocking a template from being made private, or null.
 *
 * `PUT /template-processors/{slug}` refuses that one visibility change with a
 * 409 naming the lenses that still hold the template, and the names ARE the
 * message — they tell the publisher exactly where to go next. `humanizeError`
 * can only say "this conflicts with the current state", so the caller asks here
 * instead and renders the answer against the field that caused it. Reading a
 * raw message is confined to this module for the same reason `humanizeError`
 * lives here.
 */
export function templatePrivacyBlockers(error: unknown): string[] | null {
  const raw = rawMessage(error);
  const match = /cannot be made private while these lenses use it:\s*([^.]+)/i.exec(raw);
  if (!match) return null;
  const aliases = match[1].split(',').map((alias) => alias.trim()).filter(Boolean);
  return aliases.length > 0 ? aliases : null;
}

/** The variable an environment refusal names, and which of its two fields was refused. */
export interface EnvironmentRefusal {
  name: string;
  field: 'name' | 'value';
}

/**
 * Which environment variable the API refused a version for, or null.
 *
 * The publish wizard checks the same rules before it submits, so this is the
 * backstop for what only the server can know — chiefly a kept secret with no
 * saved value behind it. Every `INVALID_ENVIRONMENT` sentence in
 * api/src/services/runtimeEnvironment.ts that names a variable quotes its name
 * last, and says "value" only when the value was refused; a sentence that names
 * none (the count cap, the list's shape) returns null. The wizard puts the
 * refusal under that field rather than in a banner that names nothing.
 */
export function environmentRefusal(error: unknown): EnvironmentRefusal | null {
  if (transportFields(error).code !== 'INVALID_ENVIRONMENT') return null;
  const raw = rawMessage(error);
  const quoted = [...raw.matchAll(/"([^"]+)"/g)];
  if (quoted.length === 0) return null;
  return { name: quoted[quoted.length - 1][1], field: /\bvalue\b/i.test(raw) ? 'value' : 'name' };
}

/** Why the API refused a parent event type (api/src/tags.ts PARENT_REJECTION_MESSAGES). */
export type TagParentRejection = 'cycle' | 'self' | 'not_found';

/**
 * Which of the three parent refusals the API answered a tag write with, or
 * null for any other error. The refusal belongs under the Parent event type
 * field, not in the dialog's generic banner, so the page reads it here — the
 * same narrow-helper pattern as `templatePrivacyBlockers`.
 */
export function tagParentRejection(error: unknown): TagParentRejection | null {
  const raw = rawMessage(error);
  if (!/'parentId'/.test(raw)) return null;
  if (/must not create a cycle/i.test(raw)) return 'cycle';
  if (/must not reference the tag itself/i.test(raw)) return 'self';
  if (/must reference an existing tag/i.test(raw)) return 'not_found';
  return null;
}

/** Why a device login's username or password was refused (the portal checks the same rules first). */
export type DeviceLoginProblem = 'missing' | 'colon' | 'too_long' | 'control_character' | 'invalid_character';

/** Why a sensor's connection config was refused. */
export type ConnectionConfigProblem = 'login_key' | 'login_in_url' | 'invalid_stream_url';

/**
 * A sensor write's refusal that belongs under one field of the sensor form: the
 * device login's username or password (`INVALID_CREDENTIALS`), or the
 * connection config (`CREDENTIALS_IN_CONNECTION_CONFIG`, `INVALID_STREAM_URL`)
 * with the dotted key path the API quoted (`auth.user`, `streams[0].url`).
 */
export type SensorFieldRefusal =
  | { field: 'username' | 'password'; problem: DeviceLoginProblem }
  | { field: 'connectionConfig'; problem: ConnectionConfigProblem; path: string | null };

/**
 * The fields a refused sensor write (POST /sensors, PUT /sensors/{id}) names,
 * or null for any other error or a refusal whose wording names no field — that
 * one goes to the form's banner through humanizeError.
 *
 * The code decides which refusal it is; wording only picks the field and the
 * reason inside the code, the same narrow-helper pattern as
 * `tagParentRejection`. The API quotes a key path and never a value, so the
 * path is the one detail read out; the sentences shown are the portal's own
 * (components/sensor/sensorForm.ts), never the API's.
 */
export function sensorFieldRefusals(error: unknown): SensorFieldRefusal[] | null {
  const { code } = transportFields(error);
  const raw = rawMessage(error);
  const path = /"([^"]+)"/.exec(raw)?.[1] ?? null;

  if (code === 'CREDENTIALS_IN_CONNECTION_CONFIG') {
    const problem: ConnectionConfigProblem = /user:pass@|contains a login/i.test(raw) ? 'login_in_url' : 'login_key';
    return [{ field: 'connectionConfig', problem, path }];
  }
  if (code === 'INVALID_STREAM_URL') {
    return [{ field: 'connectionConfig', problem: 'invalid_stream_url', path }];
  }
  if (code !== 'INVALID_CREDENTIALS') return null;

  const problem: DeviceLoginProblem | null = /colon/i.test(raw)
    ? 'colon'
    : /control character/i.test(raw)
      ? 'control_character'
      : /invalid character/i.test(raw)
        ? 'invalid_character'
      : /characters or fewer|at most|too long/i.test(raw)
        ? 'too_long'
        : /\benter\b|required|empty|missing/i.test(raw)
          ? 'missing'
          : null;
  if (!problem) return null;
  // Only a username is refused for a colon (the credential set joins the two with one).
  if (problem === 'colon') return [{ field: 'username', problem }];
  const fields = (['username', 'password'] as const).filter((field) => new RegExp(`\\b${field}\\b`, 'i').test(raw));
  return fields.length > 0 ? fields.map((field) => ({ field, problem })) : null;
}

/**
 * The number of subscriptions behind the API's refusal to delete an app client
 * ("App client still has 3 active subscription(s); delete them before deleting
 * the app client", api/src/appClients.ts), or null for any other error.
 *
 * Read so the app details dialog can take the user to those subscriptions; the
 * sentence the user sees still comes from humanizeError.
 */
export function appClientSubscriptionBlocker(error: unknown): number | null {
  const raw = rawMessage(error);
  const match = /still has (\d+) active subscription/i.exec(raw);
  return match ? Number(match[1]) : null;
}
